import React, {useContext, useEffect, useState} from 'react';
import styles from '../../styles/dashboard.module.css';
import Header from "../../components/header/header";
import PageTitle from "../../components/page_title/page_title";
import GPSTabs from "./components/gps_tabs";
import axiosInstance from "../../AxiosInstance";
import Modal from "../../components/modal/modal";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTrash, faPencil, faPlus} from "@fortawesome/free-solid-svg-icons";
import {PlaceholderInput, PlaceholderTextarea} from "../../components/input/PlaceholderTextarea";
import {useTranslation} from "react-i18next";
import MyContext from "../../Context";

const GPS_PLACEHOLDERS = ['{asset_name}', '{identification_number}', '{device_sn}'];

const ACTION_TYPE_OPTIONS = [
    {id: 1, name: 'Email'},
    {id: 3, name: 'SMS'},
    {id: 4, name: 'WhatsApp'},
    {id: 5, name: 'MQTT'},
    {id: 6, name: 'API Call'},
];

const EMPTY_FORM = {
    name: '',
    device_type: '',
    action_type: 5,
    data_1: '',
    data_2: '',
    data_3: '',
    mqtt_connection: '',
    api_connection: '',
    boolean_format: 'raw',
    is_sos: false,
};

const inputStyle = {
    width: '100%', padding: '7px 10px', borderRadius: '6px',
    border: '1px solid #CBD5E1', fontSize: '13px',
    outline: 'none', marginBottom: '4px', boxSizing: 'border-box',
};

const labelStyle = {
    fontSize: '12px', fontWeight: '600', color: '#475569',
    display: 'block', marginBottom: '3px', marginTop: '10px',
};

function getActionTypeLabel(type) {
    return ACTION_TYPE_OPTIONS.find(o => o.id === type)?.name || type;
}

function getActionDetail(action) {
    if (action.action_type === 5) return action.data_1 || '—'; // MQTT topic
    if (action.action_type === 6) return action.data_1 || '—'; // API endpoint
    if (action.action_type === 1) return action.data_1 || '—'; // Email to
    if (action.action_type === 3 || action.action_type === 4) return action.data_1 || '—'; // Phone
    return '—';
}

export default function GPSActions() {
    const {t} = useTranslation();
    const {systemSettings} = useContext(MyContext);
    const visibleActionTypes = ACTION_TYPE_OPTIONS.filter(o =>
        (o.id !== 3 || systemSettings?.sms_enabled) &&
        (o.id !== 4 || systemSettings?.whatsapp_enabled)
    );
    const project_id = parseInt(localStorage.getItem('project_id'));
    const [actions, setActions] = useState([]);
    const [deviceTypes, setDeviceTypes] = useState([]);
    const [mqttConnections, setMqttConnections] = useState([]);
    const [apiConnections, setApiConnections] = useState([]);
    const [filterDeviceType, setFilterDeviceType] = useState('');
    const [searchName, setSearchName] = useState('');

    const [showCreate, setShowCreate] = useState(false);
    const [createForm, setCreateForm] = useState(EMPTY_FORM);

    const [showEdit, setShowEdit] = useState(false);
    const [editForm, setEditForm] = useState(EMPTY_FORM);
    const [editingId, setEditingId] = useState(null);

    const [showDelete, setShowDelete] = useState(false);
    const [deletingAction, setDeletingAction] = useState(null);

    const fetchData = (mounted) => {
        if (!project_id) return;
        axiosInstance.get(`gps_action/?project_id=${project_id}`).then(res => { if (mounted.current) setActions(Array.isArray(res.data) ? res.data : (res.data.results || [])); });
        axiosInstance.get(`gps_asset/?project_id=${project_id}`).then(res => {
            const assets = Array.isArray(res.data) ? res.data : (res.data.results || []);
            const typeMap = {};
            for (const asset of assets) {
                const dtId = asset.active_gps_device?.device_type_id;
                const dtName = asset.active_gps_device?.device_type_name;
                if (dtId && dtName && !typeMap[dtId]) typeMap[dtId] = { id: dtId, name: dtName };
            }
            if (mounted.current) setDeviceTypes(Object.values(typeMap).sort((a, b) => a.name.localeCompare(b.name)));
        });
        axiosInstance.get(`mqtt_connection/?project_id=${project_id}`).then(res => { if (mounted.current) setMqttConnections(Array.isArray(res.data) ? res.data : (res.data.results || [])); }).catch(() => {});
        axiosInstance.get(`api_connection/?project_id=${project_id}`).then(res => { if (mounted.current) setApiConnections(Array.isArray(res.data) ? res.data : (res.data.results || [])); }).catch(() => {});
    };

    useEffect(() => {
        const mounted = {current: true};
        fetchData(mounted);
        return () => { mounted.current = false; };
    }, [project_id]);

    const openCreate = () => {
        setCreateForm(EMPTY_FORM);
        setShowCreate(true);
    };

    const handleCreate = () => {
        if (!createForm.name || !createForm.device_type) return;
        axiosInstance.post('gps_action/', {
            ...createForm,
            project: project_id,
            device_type: parseInt(createForm.device_type),
            action_type: parseInt(createForm.action_type),
            mqtt_connection: createForm.mqtt_connection ? parseInt(createForm.mqtt_connection) : null,
            api_connection: createForm.api_connection ? parseInt(createForm.api_connection) : null,
            is_sos: createForm.is_sos,
        }).then(() => {
            setShowCreate(false);
            fetchData();
        });
    };

    const openEdit = (action) => {
        setEditingId(action.id);
        setEditForm({
            name: action.name,
            device_type: String(action.device_type),
            action_type: action.action_type,
            data_1: action.data_1 || '',
            data_2: action.data_2 || '',
            data_3: action.data_3 || '',
            mqtt_connection: action.mqtt_connection ? String(action.mqtt_connection) : '',
            api_connection: action.api_connection ? String(action.api_connection) : '',
            boolean_format: action.boolean_format || 'raw',
            is_sos: action.is_sos || false,
        });
        setShowEdit(true);
    };

    const handleEdit = () => {
        if (!editForm.name || !editForm.device_type) return;
        axiosInstance.put(`gps_action/${editingId}/`, {
            ...editForm,
            project: project_id,
            device_type: parseInt(editForm.device_type),
            action_type: parseInt(editForm.action_type),
            mqtt_connection: editForm.mqtt_connection ? parseInt(editForm.mqtt_connection) : null,
            api_connection: editForm.api_connection ? parseInt(editForm.api_connection) : null,
            is_sos: editForm.is_sos,
        }).then(() => {
            setShowEdit(false);
            fetchData();
        });
    };

    const handleDelete = () => {
        if (!deletingAction) return;
        axiosInstance.delete(`gps_action/${deletingAction.id}/`).then(() => {
            setShowDelete(false);
            setDeletingAction(null);
            fetchData();
        });
    };

    const filteredActions = actions.filter(a => {
        if (filterDeviceType && String(a.device_type) !== filterDeviceType) return false;
        if (searchName.trim() && !a.name.toLowerCase().includes(searchName.trim().toLowerCase())) return false;
        return true;
    });

    const renderActionForm = (form, setForm, existingActions, editingId) => {
        const type = parseInt(form.action_type);
        const sosAlreadyTaken = form.device_type
            ? existingActions.some(a => a.is_sos && String(a.device_type) === String(form.device_type) && a.id !== editingId)
            : false;
        return (
            <div>
                <label style={labelStyle}>{t('gps.actions.actionName')}</label>
                <input
                    value={form.name}
                    onChange={e => setForm(f => ({...f, name: e.target.value}))}
                    style={inputStyle}
                    placeholder={t('gps.actions.actionNamePlaceholder')}
                />

                <label style={labelStyle}>{t('gps.actions.deviceType')}</label>
                <select
                    value={form.device_type}
                    onChange={e => setForm(f => ({...f, device_type: e.target.value}))}
                    style={inputStyle}
                >
                    <option value="">{t('gps.actions.selectDeviceType')}</option>
                    {deviceTypes.map(dt => (
                        <option key={dt.id} value={dt.id}>{dt.name}</option>
                    ))}
                </select>

                <label style={labelStyle}>{t('gps.actions.actionType')}</label>
                <select
                    value={form.action_type}
                    onChange={e => setForm(f => ({...f, action_type: parseInt(e.target.value), data_1: '', data_2: '', data_3: '', mqtt_connection: '', api_connection: ''}))}
                    style={inputStyle}
                >
                    {visibleActionTypes.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>

                {type === 1 && (<>
                    <label style={labelStyle}>{t('gps.assets.emailAddress')}</label>
                    <PlaceholderInput value={form.data_1} onChange={v => setForm(f => ({...f, data_1: v}))} style={inputStyle} placeholder="recipient@email.com" placeholders={GPS_PLACEHOLDERS}/>
                    <label style={labelStyle}>{t('gps.assets.subject')}</label>
                    <PlaceholderInput value={form.data_2} onChange={v => setForm(f => ({...f, data_2: v}))} style={inputStyle} placeholder="Alert: {asset_name}" placeholders={GPS_PLACEHOLDERS}/>
                    <label style={labelStyle}>{t('gps.assets.body')}</label>
                    <PlaceholderTextarea value={form.data_3} onChange={v => setForm(f => ({...f, data_3: v}))} style={{...inputStyle, minHeight: '70px', resize: 'vertical'}} placeholder="Message for {asset_name}" placeholders={GPS_PLACEHOLDERS}/>
                </>)}

                {(type === 3 || type === 4) && (<>
                    <label style={labelStyle}>{t('gps.assets.phoneNumber')}</label>
                    <PlaceholderInput value={form.data_1} onChange={v => setForm(f => ({...f, data_1: v}))} style={inputStyle} placeholder="+1234567890" placeholders={GPS_PLACEHOLDERS}/>
                    <label style={labelStyle}>{t('gps.assets.message')}</label>
                    <PlaceholderTextarea value={form.data_3} onChange={v => setForm(f => ({...f, data_3: v}))} style={{...inputStyle, minHeight: '70px', resize: 'vertical'}} placeholder="Message for {asset_name}" placeholders={GPS_PLACEHOLDERS}/>
                </>)}

                {type === 5 && (<>
                    <label style={labelStyle}>{t('gps.assets.mqttConnection')}</label>
                    <select value={form.mqtt_connection} onChange={e => setForm(f => ({...f, mqtt_connection: e.target.value}))} style={inputStyle}>
                        <option value="">{t('gps.assets.selectConnection')}</option>
                        {mqttConnections.map(c => <option key={c.id} value={c.id}>{c.name || c.host}</option>)}
                    </select>
                    <label style={labelStyle}>{t('gps.assets.topic')}</label>
                    <PlaceholderInput value={form.data_1} onChange={v => setForm(f => ({...f, data_1: v}))} style={inputStyle} placeholder="cmd/{asset_name}" placeholders={GPS_PLACEHOLDERS}/>
                    <label style={labelStyle}>{t('gps.assets.payload')}</label>
                    <PlaceholderTextarea value={form.data_3} onChange={v => setForm(f => ({...f, data_3: v}))} style={{...inputStyle, minHeight: '80px', resize: 'vertical', fontFamily: 'monospace'}} placeholder='{"cmd":"action","id":"{identification_number}"}' placeholders={GPS_PLACEHOLDERS}/>
                    <label style={labelStyle}>{t('gps.assets.boolFormat')}</label>
                    <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px'}}>
                        {[{v: 'raw', l: '0 / 1'}, {v: 'bool', l: 'True / False'}, {v: 'lower', l: 'true / false'}].map(opt => (
                            <label key={opt.v} style={{display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', cursor: 'pointer', padding: '4px 10px', borderRadius: '6px', border: `1px solid ${form.boolean_format === opt.v ? '#4F46E5' : '#E2E8F0'}`, background: form.boolean_format === opt.v ? '#EEF2FF' : '#F8FAFC', color: form.boolean_format === opt.v ? '#4F46E5' : '#64748B', fontWeight: form.boolean_format === opt.v ? 600 : 400}}>
                                <input type="radio" name="bool_format" value={opt.v} checked={form.boolean_format === opt.v} onChange={() => setForm(f => ({...f, boolean_format: opt.v}))} style={{accentColor: '#4F46E5'}}/>
                                {opt.l}
                            </label>
                        ))}
                    </div>
                </>)}

                {type === 6 && (<>
                    <label style={labelStyle}>{t('gps.assets.apiConnection')}</label>
                    <select value={form.api_connection} onChange={e => setForm(f => ({...f, api_connection: e.target.value}))} style={inputStyle}>
                        <option value="">{t('gps.assets.selectConnection')}</option>
                        {apiConnections.map(c => <option key={c.id} value={c.id}>{c.name || c.url}</option>)}
                    </select>
                    <label style={labelStyle}>{t('gps.assets.endpointPath')}</label>
                    <PlaceholderInput value={form.data_1} onChange={v => setForm(f => ({...f, data_1: v}))} style={inputStyle} placeholder="/webhook/{identification_number}" placeholders={GPS_PLACEHOLDERS}/>
                    <label style={labelStyle}>{t('gps.assets.httpMethod')}</label>
                    <select value={form.data_2 || 'POST'} onChange={e => setForm(f => ({...f, data_2: e.target.value}))} style={inputStyle}>
                        {['POST', 'PUT', 'GET', 'PATCH'].map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <label style={labelStyle}>{t('gps.assets.bodyJson')}</label>
                    <PlaceholderTextarea value={form.data_3} onChange={v => setForm(f => ({...f, data_3: v}))} style={{...inputStyle, minHeight: '80px', resize: 'vertical', fontFamily: 'monospace'}} placeholder='{"id":"{identification_number}"}' placeholders={GPS_PLACEHOLDERS}/>
                    <label style={labelStyle}>{t('gps.assets.boolFormat')}</label>
                    <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px'}}>
                        {[{v: 'raw', l: '0 / 1'}, {v: 'bool', l: 'True / False'}, {v: 'lower', l: 'true / false'}].map(opt => (
                            <label key={opt.v} style={{display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', cursor: 'pointer', padding: '4px 10px', borderRadius: '6px', border: `1px solid ${form.boolean_format === opt.v ? '#4F46E5' : '#E2E8F0'}`, background: form.boolean_format === opt.v ? '#EEF2FF' : '#F8FAFC', color: form.boolean_format === opt.v ? '#4F46E5' : '#64748B', fontWeight: form.boolean_format === opt.v ? 600 : 400}}>
                                <input type="radio" name="bool_format" value={opt.v} checked={form.boolean_format === opt.v} onChange={() => setForm(f => ({...f, boolean_format: opt.v}))} style={{accentColor: '#4F46E5'}}/>
                                {opt.l}
                            </label>
                        ))}
                    </div>
                </>)}

                <div style={{marginTop: '14px', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${sosAlreadyTaken ? '#FECACA' : '#E2E8F0'}`, background: sosAlreadyTaken ? '#FFF5F5' : '#F8FAFC'}}>
                    <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: sosAlreadyTaken ? 'not-allowed' : 'pointer', margin: 0}}>
                        <input
                            type="checkbox"
                            checked={form.is_sos}
                            disabled={sosAlreadyTaken}
                            onChange={e => setForm(f => ({...f, is_sos: e.target.checked}))}
                            style={{accentColor: '#EF4444', width: '15px', height: '15px', cursor: sosAlreadyTaken ? 'not-allowed' : 'pointer'}}
                        />
                        <span style={{fontSize: '13px', fontWeight: '600', color: sosAlreadyTaken ? '#EF4444' : '#334155'}}>
                            SOS — Acción de emergencia
                        </span>
                    </label>
                    {sosAlreadyTaken && (
                        <p style={{margin: '6px 0 0 23px', fontSize: '12px', color: '#EF4444'}}>
                            Ya existe una acción SOS para este tipo de dispositivo.
                        </p>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className={styles.dashboard_container}>
            <Header sidebarIndex={1}/>
            <Modal
                show={showCreate}
                setter={setShowCreate}
                name={t('gps.actions.newAction')}
                buttonName={t('common.create')}
                onClick={handleCreate}
            >
                {renderActionForm(createForm, setCreateForm, actions, null)}
            </Modal>

            <Modal
                show={showEdit}
                setter={setShowEdit}
                name={t('gps.actions.editAction')}
                buttonName={t('common.save')}
                onClick={handleEdit}
            >
                {renderActionForm(editForm, setEditForm, actions, editingId)}
            </Modal>

            <Modal
                show={showDelete}
                setter={(val) => { setShowDelete(val); if (!val) setDeletingAction(null); }}
                name={t('gps.actions.deleteAction')}
                buttonName={t('common.delete')}
                onClick={handleDelete}
                danger
            >
                <p style={{margin: 0, fontSize: '14px'}}>
                    {t('gps.actions.deleteConfirm', {name: deletingAction?.name})}
                </p>
            </Modal>

            <div className={styles.dashboardInnerContainer}>
                <PageTitle title={t('gps.actions.title')}/>
                <GPSTabs active={4}/>

                <div className="d-flex align-items-center justify-content-between mb-3 gap-2 flex-wrap">
                    <div className="d-flex align-items-center gap-2" style={{flexWrap: 'wrap'}}>
                        <input
                            type="text"
                            value={searchName}
                            onChange={e => setSearchName(e.target.value)}
                            placeholder={t('gps.actions.searchByName')}
                            style={{padding: '6px 10px', border: '1px solid #CBD5E1', borderRadius: '6px', fontSize: '13px', outline: 'none', color: '#334155', minWidth: '140px', flex: '1 1 140px'}}
                        />
                        <select
                            value={filterDeviceType}
                            onChange={e => setFilterDeviceType(e.target.value)}
                            style={{padding: '6px 10px', border: '1px solid #CBD5E1', borderRadius: '6px', fontSize: '13px', outline: 'none', color: '#334155', minWidth: '0', flex: '1 1 120px'}}
                        >
                            <option value="">{t('gps.actions.allDeviceTypes')}</option>
                            {deviceTypes.map(dt => <option key={dt.id} value={String(dt.id)}>{dt.name}</option>)}
                        </select>
                        <span style={{fontSize: '13px', color: '#64748B', whiteSpace: 'nowrap'}}>
                            {t('gps.actions.actionsCount', {count: filteredActions.length})}
                        </span>
                    </div>
                    <button
                        onClick={openCreate}
                        style={{padding: '6px 14px', border: 'none', borderRadius: '6px', backgroundColor: '#4F46E5', color: 'white', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px'}}
                    >
                        <FontAwesomeIcon icon={faPlus} size="sm"/> {t('gps.actions.newAction')}
                    </button>
                </div>

                {filteredActions.length === 0 ? (
                    <div style={{textAlign: 'center', padding: '48px 0', color: '#94A3B8', fontSize: '14px'}}>
                        {t('gps.actions.noActions')}
                    </div>
                ) : (
                    <div style={{border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden', overflowX: 'auto'}}>
                        <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '520px'}}>
                            <thead>
                                <tr style={{backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0'}}>
                                    <th style={{padding: '10px 16px', textAlign: 'left', fontWeight: '600', color: '#475569'}}>{t('gps.actions.name')}</th>
                                    <th style={{padding: '10px 16px', textAlign: 'left', fontWeight: '600', color: '#475569'}}>{t('gps.actions.deviceType')}</th>
                                    <th style={{padding: '10px 16px', textAlign: 'left', fontWeight: '600', color: '#475569'}}>{t('gps.actions.actionType')}</th>
                                    <th style={{padding: '10px 16px', textAlign: 'left', fontWeight: '600', color: '#475569'}}>{t('gps.actions.detail')}</th>
                                    <th style={{padding: '10px 16px', width: '80px'}}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredActions.map((action, idx) => (
                                    <tr key={action.id} style={{borderBottom: idx < filteredActions.length - 1 ? '1px solid #F1F5F9' : 'none', background: 'white'}}>
                                        <td style={{padding: '10px 16px', fontWeight: '500', color: '#1E293B'}}>
                                            <span style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                                                {action.name}
                                                {action.is_sos && (
                                                    <span style={{padding: '1px 6px', borderRadius: '4px', background: '#FEE2E2', color: '#EF4444', fontSize: '11px', fontWeight: '700', letterSpacing: '0.05em'}}>SOS</span>
                                                )}
                                            </span>
                                        </td>
                                        <td style={{padding: '10px 16px', color: '#475569'}}>
                                            <span style={{padding: '2px 8px', borderRadius: '4px', background: '#F1F5F9', fontSize: '12px', fontWeight: '500'}}>
                                                {action.device_type_name}
                                            </span>
                                        </td>
                                        <td style={{padding: '10px 16px', color: '#475569'}}>{getActionTypeLabel(action.action_type)}</td>
                                        <td style={{padding: '10px 16px', color: '#64748B', fontFamily: action.action_type >= 5 ? 'monospace' : 'inherit', fontSize: action.action_type >= 5 ? '12px' : '13px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                            {getActionDetail(action)}
                                        </td>
                                        <td style={{padding: '10px 16px', textAlign: 'right'}}>
                                            <div style={{display: 'flex', gap: '6px', justifyContent: 'flex-end'}}>
                                                <button
                                                    onClick={() => openEdit(action)}
                                                    style={{padding: '4px 8px', border: '1px solid #CBD5E1', borderRadius: '5px', background: 'white', cursor: 'pointer', color: '#475569'}}
                                                    title={t('common.edit')}
                                                >
                                                    <FontAwesomeIcon icon={faPencil} size="xs"/>
                                                </button>
                                                <button
                                                    onClick={() => { setDeletingAction(action); setShowDelete(true); }}
                                                    style={{padding: '4px 8px', border: '1px solid #FECACA', borderRadius: '5px', background: '#FFF5F5', cursor: 'pointer', color: '#EF4444'}}
                                                    title={t('common.delete')}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} size="xs"/>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
